import {useEffect, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';

import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {Box, Button, Grid} from '@mui/material';

import {MatchFilterOptions} from '../matches/MatchFilters/MatchFilters';

import {AutoCompleteFilter} from '../../../../components/AutoCompleteFilter/AutoCompleteFilter';
import {EmpionTooltip} from '../../../../components/EmpionTooltip/EmpionTooltip';
import {greenColors} from '../../../../layouts/theme/designSystem';
import {Option} from '../../../../types/shared-types';

interface Props {
  jobOptions: Option[];
  statusOptions: Option[];
  initialJobs: Option[];
  initialStatuses: Option[];
  filterBy: (filter: MatchFilterOptions) => void;
}

export const TalentPoolFilters = ({
  jobOptions,
  statusOptions,
  filterBy,
  initialStatuses,
  initialJobs,
}: Props) => {
  const {t} = useTranslation();
  const [filters, setFilters] = useState<MatchFilterOptions>({job: [], status: []});
  /** Remount autocompletes so internal selection clears after URL-driven `initial*` props are empty. */
  const [filterInstanceKey, setFilterInstanceKey] = useState(0);
  const hadListFiltersRef = useRef(false);

  useEffect(() => {
    const hasListFilters = initialJobs.length > 0 || initialStatuses.length > 0;
    if (hadListFiltersRef.current && !hasListFilters) {
      setFilterInstanceKey((k) => k + 1);
    }
    hadListFiltersRef.current = hasListFilters;
  }, [initialJobs, initialStatuses]);

  const onStatusSelect = (options: Option[]) => {
    const update = {...filters, status: [...options]};
    setFilters(update);
    filterBy(update);
  };

  const onCampaignSelect = (options: Option[]) => {
    const update = {...filters, job: [...options]};
    setFilters(update);
    filterBy(update);
  };

  const clearFilters = () => {
    const empty = {job: [] as Option[], status: [] as Option[]};
    setFilters(empty);
    filterBy(empty);
  };

  return (
    <Grid container gap='16px' flexWrap='wrap' alignItems='flex-end'>
      <AutoCompleteFilter
        key={`tp-campaign-${filterInstanceKey}`}
        sx={{width: {xs: '100%', sm: '401px'}, '& .MuiOutlinedInput-root': {minHeight: 46}}}
        label={String(t('talentPoolPage.filters.allCampaigns'))}
        options={jobOptions}
        currentOptions={initialJobs}
        onSelect={onCampaignSelect}
      />
      <AutoCompleteFilter
        key={`tp-status-${filterInstanceKey}`}
        sx={{width: {xs: '100%', sm: '223px'}, '& .MuiOutlinedInput-root': {minHeight: 46}}}
        label={String(t('talentPoolPage.filters.allStatuses'))}
        options={statusOptions}
        currentOptions={initialStatuses}
        onSelect={onStatusSelect}
      />
      <Box sx={{width: {xs: '100%', sm: 'auto'}, display: 'flex', alignItems: 'flex-end'}}>
        <EmpionTooltip title={String(t('talentPoolPage.filters.clear'))} isEnabled placement='top'>
          <Button
            type='button'
            variant='outlined'
            aria-label={String(t('talentPoolPage.filters.clear'))}
            onClick={clearFilters}
            sx={{
              minWidth: 40,
              width: 40,
              height: 40,
              borderRadius: '5px',
              textTransform: 'none',
              borderColor: greenColors[700],
              color: 'text.primary',
            }}
          >
            <RestartAltIcon fontSize='small' />
          </Button>
        </EmpionTooltip>
      </Box>
    </Grid>
  );
};
